import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, MapPin, FileText, Users, Send,
  Eye, Download, CheckCircle, AlertTriangle,
  Clock, Lock, MessageSquare, ChevronRight,
  Shield, Paperclip, X,
  ThumbsUp, ThumbsDown, MessageCircle, HelpCircle,
  ClipboardCheck, RefreshCw,
} from 'lucide-react';
import {
  getDossier, assignerAgent, terminerEtape,
  validerEtape, getAgents, cloturerDossier,
  repondreDossier, commenter,
} from '../api/dossiers';
import { getDocuments }  from '../api/documents';
import DocumentUpload    from '../components/DocumentUpload';
import StepTracker       from '../components/StepTracker';
import useAuthStore      from '../store/authStore';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
dayjs.locale('fr');

const C = {
  sand:'#f5ede0', terra:'#c2521a', terraLt:'#e0784a', terraDk:'#8a3510',
  forest:'#1a3d28', forestMid:'#244d35', forestLt:'#2f6645',
  gold:'#c8902a', goldLt:'#e8b84a', ink:'#0e1a10', mist:'#6e8070', cream:'#faf6ef',
};

const STATUS_STYLE = {
  SOUMIS:                  { bg:'rgba(200,144,42,.1)',  color:'#8a6010',  dot:'#c8902a', label:'Soumis' },
  VERIFICATION_CADASTRALE: { bg:'rgba(124,107,191,.1)', color:'#5a4baf',  dot:'#7c6bbf', label:'Vérif. cadastrale' },
  ENQUETE_TERRAIN:         { bg:'rgba(194,82,26,.1)',   color:C.terraDk,  dot:C.terra,   label:'Enquête terrain' },
  VERIFICATION_TITRES:     { bg:'rgba(42,143,168,.1)',  color:'#1a6a82',  dot:'#2a8fa8', label:'Vérif. titres' },
  RAPPORT_EXPERTISE:       { bg:'rgba(47,102,69,.1)',   color:C.forestLt, dot:C.forestLt,label:'Rapport expertise' },
  CLOTURE:                 { bg:'rgba(110,128,112,.1)', color:C.mist,     dot:C.mist,    label:'Clôturé' },
};
const RESULTAT_STYLE = {
  EN_ATTENTE:{ bg:'rgba(200,144,42,.1)',  color:'#8a6010',  label:'En attente', icon:'⏳' },
  VALIDE:    { bg:'rgba(47,102,69,.12)',  color:C.forestLt, label:'Validé',     icon:'✓'  },
  LITIGE:    { bg:'rgba(194,82,26,.12)',  color:C.terraDk,  label:'Litige',     icon:'⚠'  },
  RISQUE:    { bg:'rgba(194,82,26,.08)',  color:C.terra,    label:'Risque',     icon:'🔶' },
};
const DECISION_STYLE = {
  ACCORD:      { bg:'rgba(47,102,69,.1)',   color:C.forestLt, label:'Souhaite acheter',     icon:<ThumbsUp size={13}/> },
  RENONCIATION:{ bg:'rgba(194,82,26,.1)',   color:C.terraDk,  label:"Renonce à l'achat",    icon:<ThumbsDown size={13}/> },
  NEGOCIATION: { bg:'rgba(200,144,42,.1)',  color:'#8a6010',  label:'Veut négocier',         icon:<MessageCircle size={13}/> },
  INFO_SUPP:   { bg:'rgba(110,128,112,.1)', color:C.mist,     label:'Infos complémentaires', icon:<HelpCircle size={13}/> },
};

/* ─── CSS ─── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=DM+Sans:wght@400;500;600;700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'DM Sans',sans-serif;}

.ddp { min-height:100vh; padding:26px 36px 56px; }
@media(max-width:768px){ .ddp{ padding:16px 14px 44px; } }

/* ── TOP BAR ── */
.ddp-bar { display:flex; align-items:center; gap:10px; margin-bottom:18px; flex-wrap:wrap; }
.btn-bk  { display:inline-flex;align-items:center;gap:7px;padding:8px 15px;border-radius:11px;border:1.5px solid rgba(26,61,40,.13);background:transparent;font:600 .83rem 'DM Sans',sans-serif;color:${C.mist};cursor:pointer;transition:all .2s; }
.btn-bk:hover{ border-color:${C.forest};color:${C.forest};background:rgba(26,61,40,.03); }
.ddp-ref { font-family:'Cormorant Garamond',serif;font-size:clamp(1.4rem,2.8vw,2.1rem);font-weight:700;letter-spacing:-.03em;color:${C.ink}; }
.tag { display:inline-flex;align-items:center;gap:5px;padding:4px 11px;border-radius:999px;font-size:.7rem;font-weight:700;white-space:nowrap; }
.tag-dot { width:5px;height:5px;border-radius:50%;flex:none; }
.tag-pulse { animation:tpulse 2s ease-in-out infinite; }
@keyframes tpulse{0%,100%{box-shadow:0 0 0 0 rgba(200,144,42,.5);}50%{box-shadow:0 0 0 6px rgba(200,144,42,0);}}

/* ── PROGRESS BANNER ── */
.prog-banner {
  background:white;
  border-radius:22px;
  border:1px solid rgba(26,61,40,.07);
  box-shadow:0 6px 28px rgba(26,61,40,.07);
  padding:22px 26px 18px;
  margin-bottom:20px;
}
.prog-eyebrow {
  font-size:.65rem;font-weight:800;letter-spacing:.2em;text-transform:uppercase;
  color:${C.mist};margin-bottom:14px;
  display:flex;align-items:center;gap:8px;
}
.prog-eyebrow::before{content:'';display:inline-block;width:18px;height:2.5px;border-radius:999px;background:${C.terra};}

/* ── RAPPORT STRIP ── */
.rapport-strip {
  display:flex;align-items:center;gap:14px;
  padding:14px 18px;border-radius:16px;
  background:linear-gradient(135deg,rgba(200,144,42,.08),rgba(194,82,26,.06));
  border:1.5px solid rgba(200,144,42,.25);
  cursor:pointer;margin-bottom:20px;
  transition:transform .25s,box-shadow .25s;
  text-decoration:none;
}
.rapport-strip:hover{ transform:translateY(-2px);box-shadow:0 10px 26px rgba(200,144,42,.15); }
.r-icon{ width:40px;height:40px;border-radius:11px;background:linear-gradient(135deg,${C.gold},${C.terra});display:grid;place-items:center;flex:none;box-shadow:0 5px 14px rgba(200,144,42,.28); }
.r-title{ font-family:'Cormorant Garamond',serif;font-size:1rem;font-weight:700;color:${C.ink}; }
.r-sub  { font-size:.74rem;color:${C.mist};margin-top:1px; }

/* ── BODY GRID ── */
.ddp-body {
  display:grid;
  grid-template-columns:minmax(0,1.15fr) minmax(0,1fr) 296px;
  gap:16px;
  align-items:start;
}
@media(max-width:1180px){ .ddp-body{ grid-template-columns:1fr 1fr; } }
@media(max-width:750px) { .ddp-body{ grid-template-columns:1fr; } }

/* ── CARD ── */
.card{ background:white;border-radius:20px;border:1px solid rgba(26,61,40,.07);box-shadow:0 3px 14px rgba(26,61,40,.05);overflow:hidden;margin-bottom:16px; }
.card:last-child{ margin-bottom:0; }
.card.bdr-forest{ border-color:rgba(26,61,40,.2); }
.card.bdr-gold  { border-color:rgba(200,144,42,.3); }
.card.bdr-green { border-color:rgba(47,102,69,.28); }
.card.bdr-terra { border-color:rgba(194,82,26,.22); }
.ch  { padding:15px 18px 13px;border-bottom:1px solid rgba(26,61,40,.06);display:flex;align-items:center;gap:9px; }
.chi { width:32px;height:32px;border-radius:9px;display:grid;place-items:center;flex:none; }
.cht { font-family:'Cormorant Garamond',serif;font-size:1.05rem;font-weight:700;color:${C.ink}; }
.chb { margin-left:auto;background:rgba(26,61,40,.07);color:${C.mist};font-size:.66rem;font-weight:700;padding:2px 8px;border-radius:999px; }
.cb  { padding:16px 18px; }

/* ── INFO GRID ── */
.ig{ display:grid;grid-template-columns:1fr 1fr;gap:11px; }
.ig-full{ grid-column:1/-1; }
.il{ font-size:.64rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:${C.mist};margin-bottom:2px; }
.iv{ font-size:.88rem;font-weight:600;color:${C.ink};line-height:1.4; }
.iv.mono{ font-family:monospace;font-size:.8rem; }
.conc{ margin-top:12px;padding:12px 14px;border-radius:12px;background:rgba(47,102,69,.06);border:1px solid rgba(47,102,69,.16); }
.conc-l{ font-size:.63rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:${C.forestLt};margin-bottom:4px; }
.conc-t{ font-size:.86rem;line-height:1.7;color:${C.forest}; }

/* ── DOCS ── */
.doc-list{ display:grid;gap:7px;margin-top:8px; }
.doc-row { display:flex;align-items:center;gap:9px;padding:9px 11px;border-radius:10px;background:${C.cream};border:1px solid rgba(26,61,40,.06);transition:border-color .2s,background .2s; }
.doc-row:hover{ border-color:rgba(26,61,40,.14);background:white; }
.doc-ico { width:30px;height:30px;border-radius:8px;background:rgba(26,61,40,.07);display:grid;place-items:center;color:${C.mist};flex:none; }
.doc-nm  { font-size:.82rem;font-weight:600;color:${C.ink};flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap; }
.doc-mt  { font-size:.68rem;color:${C.mist};margin-top:1px; }
.doc-acts{ display:flex;gap:4px;flex:none; }
.doc-btn { width:26px;height:26px;border-radius:7px;display:grid;place-items:center;border:1px solid rgba(26,61,40,.1);background:transparent;cursor:pointer;color:${C.mist};transition:background .2s,color .2s;text-decoration:none; }
.doc-btn:hover{ background:rgba(26,61,40,.07);color:${C.forest}; }
.e-hint{ text-align:center;padding:20px;color:${C.mist};font-size:.8rem; }

/* ── COMMENTS ── */
.cmt-thread{ display:grid;gap:11px;margin-bottom:13px; }
.cmt-row   { display:flex;gap:9px; }
.cmt-av    { width:30px;height:30px;border-radius:9px;background:linear-gradient(135deg,${C.forest},${C.forestLt});display:grid;place-items:center;font-family:'Cormorant Garamond',serif;font-size:.88rem;font-weight:700;color:${C.sand};flex:none; }
.cmt-bbl   { flex:1;background:${C.cream};border-radius:0 12px 12px 12px;padding:9px 12px;border:1px solid rgba(26,61,40,.06); }
.cmt-meta  { display:flex;align-items:center;gap:6px;margin-bottom:4px;flex-wrap:wrap; }
.cmt-auth  { font-size:.78rem;font-weight:700;color:${C.ink}; }
.cmt-dt    { font-size:.68rem;color:rgba(110,128,112,.5); }
.cmt-int   { background:rgba(200,144,42,.1);color:#8a6010;font-size:.6rem;font-weight:700;padding:2px 6px;border-radius:999px; }
.cmt-txt   { font-size:.83rem;line-height:1.7;color:${C.forest}; }
.cmt-compose{ display:flex;gap:8px;align-items:flex-end; }
.cmt-cav   { width:30px;height:30px;border-radius:9px;background:linear-gradient(135deg,${C.terra},${C.gold});display:grid;place-items:center;font-family:'Cormorant Garamond',serif;font-size:.88rem;font-weight:700;color:white;flex:none; }
.cmt-ta    { width:100%;border:1.5px solid rgba(26,61,40,.11);border-radius:11px;background:${C.cream};color:${C.ink};padding:9px 12px;font:500 .86rem 'DM Sans',sans-serif;outline:none;resize:vertical;min-height:66px;transition:border-color .2s,box-shadow .2s; }
.cmt-ta:focus{ border-color:${C.terra};box-shadow:0 0 0 3px rgba(194,82,26,.08);background:white; }
.cmt-ta::placeholder{ color:rgba(110,128,112,.36); }
.cmt-send  { width:34px;height:34px;border-radius:9px;background:${C.forest};border:none;display:grid;place-items:center;color:${C.sand};cursor:pointer;transition:transform .2s,box-shadow .2s;flex:none;box-shadow:0 4px 14px rgba(26,61,40,.18); }
.cmt-send:hover:not(:disabled){ transform:translateY(-2px);box-shadow:0 7px 18px rgba(26,61,40,.25); }
.cmt-send:disabled{ opacity:.4;cursor:not-allowed; }

/* ── ACTEURS ── */
.act-lbl { font-size:.62rem;font-weight:800;letter-spacing:.18em;text-transform:uppercase;color:${C.mist};margin:10px 0 6px; }
.act-lbl:first-child{ margin-top:0; }
.act-row { display:flex;align-items:center;gap:9px;padding:9px 11px;border-radius:11px;background:${C.cream};border:1px solid rgba(26,61,40,.06); }
.act-av  { width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg,${C.forest},${C.forestLt});display:grid;place-items:center;font-family:'Cormorant Garamond',serif;font-size:.9rem;font-weight:700;color:${C.sand};flex:none; }
.act-nm  { font-size:.84rem;font-weight:700;color:${C.ink}; }
.act-em  { font-size:.7rem;color:${C.mist};margin-top:1px; }
.act-role{ margin-left:auto;font-size:.62rem;font-weight:700;padding:2px 8px;border-radius:999px;background:rgba(26,61,40,.07);color:${C.mist};white-space:nowrap; }
.act-none{ display:flex;align-items:center;gap:7px;padding:9px 11px;border-radius:11px;background:rgba(194,82,26,.05);border:1px dashed rgba(194,82,26,.28);font-size:.8rem;color:${C.terraDk};font-weight:500; }
.hdiv    { height:1px;background:rgba(26,61,40,.06);margin:12px 0; }

/* ── FORMS ── */
.fl  { font-size:.62rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:${C.mist};margin-bottom:5px;display:block; }
.fi,.fs,.fta { width:100%;border:1.5px solid rgba(26,61,40,.11);border-radius:10px;background:${C.cream};color:${C.ink};padding:9px 12px;font:500 .86rem 'DM Sans',sans-serif;outline:none;transition:border-color .2s,box-shadow .2s;appearance:none;margin-bottom:10px; }
.fi:focus,.fs:focus,.fta:focus{ border-color:${C.terra};box-shadow:0 0 0 3px rgba(194,82,26,.08);background:white; }
.fs { background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='7' viewBox='0 0 11 7'%3E%3Cpath fill='%236e8070' d='M5.5 7L0 0h11z'/%3E%3C/svg%3E");background-repeat:no-repeat;background-position:right 11px center;padding-right:30px;cursor:pointer; }
.fta{ resize:vertical;min-height:75px; }
.frow{ display:flex;gap:7px; }
.frow .fs{ flex:1;margin-bottom:0; }

/* ── BUTTONS ── */
.ba { display:flex;align-items:center;justify-content:center;gap:7px;width:100%;padding:11px;border-radius:11px;font:700 .86rem 'DM Sans',sans-serif;border:none;cursor:pointer;transition:transform .25s,box-shadow .25s;position:relative;overflow:hidden; }
.ba::before{ content:'';position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,.08),transparent);transform:translateX(-100%);transition:transform .5s; }
.ba:hover::before{ transform:translateX(100%); }
.ba:hover:not(:disabled){ transform:translateY(-2px); }
.ba:disabled{ opacity:.5;cursor:not-allowed;transform:none; }
.ba-f{ background:${C.forest};color:${C.sand};box-shadow:0 7px 20px rgba(26,61,40,.2); }
.ba-f:hover:not(:disabled){ box-shadow:0 12px 26px rgba(26,61,40,.27); }
.ba-t{ background:linear-gradient(135deg,${C.terra},${C.terraLt});color:white;box-shadow:0 7px 20px rgba(194,82,26,.22); }
.ba-g{ background:linear-gradient(135deg,${C.gold},${C.goldLt});color:${C.ink};box-shadow:0 7px 20px rgba(200,144,42,.22); }
.ba-gr{ background:linear-gradient(135deg,${C.forestMid},${C.forestLt});color:${C.sand};box-shadow:0 7px 20px rgba(26,61,40,.2); }

/* ── WAITING BOX ── */
.wbox{ text-align:center;padding:14px 6px;display:grid;gap:8px;justify-items:center; }
.w-ic{ width:44px;height:44px;border-radius:50%;background:rgba(200,144,42,.1);display:grid;place-items:center;color:${C.gold};animation:tpulse 2s ease-in-out infinite; }
.w-tt{ font-family:'Cormorant Garamond',serif;font-size:1rem;font-weight:700;color:${C.ink}; }
.w-sb{ font-size:.76rem;color:${C.mist};line-height:1.6;max-width:200px; }

/* ── DECISION ── */
.dec{ padding:11px 13px;border-radius:11px;display:flex;align-items:flex-start;gap:9px; }
.dec-ic{ width:32px;height:32px;border-radius:8px;display:grid;place-items:center;flex:none; }
.dec-lb{ font-size:.83rem;font-weight:700; }
.dec-cm{ font-size:.76rem;opacity:.62;margin-top:2px;line-height:1.6; }
.dec-dt{ font-size:.67rem;opacity:.4;margin-top:5px; }

/* ── ERROR ── */
.err{ display:flex;align-items:flex-start;gap:9px;background:rgba(194,82,26,.07);border:1px solid rgba(194,82,26,.2);color:${C.terraDk};border-radius:12px;padding:11px 15px;font-size:.82rem;line-height:1.6;margin-bottom:16px; }
.err-x{ margin-left:auto;background:transparent;border:none;cursor:pointer;color:${C.terra};padding:1px;flex:none; }

.spin{ animation:spin .8s linear infinite; }
@keyframes spin{ to{ transform:rotate(360deg); } }

.nf{ text-align:center;padding:70px 20px;color:${C.mist}; }
.nf-t{ font-family:'Cormorant Garamond',serif;font-size:1.7rem;font-weight:700;color:${C.ink};margin-bottom:7px; }
`;

/* ── helpers ── */
const ini = u => ((u?.first_name?.[0]||'')+(u?.last_name?.[0]||u?.username?.[0]||'U')).toUpperCase();
const STAGGER = { hidden:{}, visible:{ transition:{ staggerChildren:.055 } } };
const ITEM    = { hidden:{opacity:0,y:12}, visible:{opacity:1,y:0,transition:{duration:.46,ease:[.22,1,.36,1]}} };

function CH({ icon:Icon, ibg, ic, title, badge }) {
  return (
    <div className="ch">
      <div className="chi" style={{ background:ibg, color:ic }}><Icon size={15}/></div>
      <span className="cht">{title}</span>
      {badge != null && <span className="chb">{badge}</span>}
    </div>
  );
}

export default function DossierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [dossier,setDossier]   = useState(null);
  const [docs,setDocs]         = useState([]);
  const [loading,setLoading]   = useState(true);
  const [error,setError]       = useState(null);
  const [agents,setAgents]     = useState([]);
  const [comment,setComment]   = useState('');
  const [agentId,setAgentId]   = useState('');
  const [valide,setValide]     = useState({ commentaire:'', resultat:'', conclusion:'' });
  const [termine,setTermine]   = useState('');
  const [cloture,setCloture]   = useState({ resultat:'', conclusion:'' });
  const [decision,setDecision] = useState({ decision:'', commentaire:'' });
  const [al,setAl]             = useState('');

  const fetch_ = async () => {
    try {
      const [d,dc] = await Promise.all([getDossier(id), getDocuments(id)]);
      setDossier(d.data); setDocs(dc.data.results||dc.data);
    } finally { setLoading(false); }
  };
  useEffect(()=>{ fetch_(); },[id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{ if(user?.role==='ADMIN') getAgents().then(({data})=>setAgents(data)); },[user]);

  const run = async (key, fn) => {
    setAl(key); setError(null);
    try { await fn(); await fetch_(); }
    catch(e){ setError(typeof e.response?.data==='object'?JSON.stringify(e.response.data,null,2):(e.response?.data||'Erreur.')); }
    finally { setAl(''); }
  };

  if (loading) return (
    <div className="ddp" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'60vh'}}>
      <style>{css}</style>
      <div style={{textAlign:'center',display:'grid',gap:10}}>
        <RefreshCw size={24} color={C.terra} className="spin" style={{margin:'0 auto'}}/>
        <span style={{fontSize:'.8rem',color:C.mist}}>Chargement…</span>
      </div>
    </div>
  );

  if (!dossier) return (
    <div className="ddp"><style>{css}</style>
      <div className="nf">
        <div className="nf-t">Dossier introuvable</div>
        <p>Ce dossier n'existe pas ou vous n'y avez pas accès.</p>
        <button className="btn-bk" style={{marginTop:16}} onClick={()=>navigate('/dossiers')}>
          <ArrowLeft size={13}/> Retour
        </button>
      </div>
    </div>
  );

  const st  = STATUS_STYLE[dossier.statut]||{bg:'rgba(110,128,112,.1)',color:C.mist,dot:C.mist,label:dossier.statut};
  const res = RESULTAT_STYLE[dossier.resultat]||RESULTAT_STYLE.EN_ATTENTE;
  const closed = dossier.statut==='CLOTURE';

  return (
    <div className="ddp">
      <style>{css}</style>
      <motion.div variants={STAGGER} initial="hidden" animate="visible">

        {/* ── TOP BAR ── */}
        <motion.div variants={ITEM} className="ddp-bar">
          <button className="btn-bk" onClick={()=>navigate('/dossiers')}>
            <ArrowLeft size={13}/> Mes dossiers
          </button>
          <span className="ddp-ref">{dossier.reference}</span>
          {dossier.urgent && (
            <span className="tag" style={{background:'rgba(194,82,26,.12)',color:C.terraDk}}>
              <AlertTriangle size={10}/> Urgent
            </span>
          )}
          <span className="tag" style={{background:st.bg,color:st.color}}>
            <span className="tag-dot" style={{background:st.dot}}/>{st.label}
          </span>
          {dossier.etape_terminee && (
            <span className="tag tag-pulse" style={{background:'rgba(200,144,42,.12)',color:'#8a6010'}}>
              <Clock size={10}/> En attente validation
            </span>
          )}
          {closed && dossier.resultat && (
            <span className="tag" style={{background:res.bg,color:res.color}}>
              {res.icon} {res.label}
            </span>
          )}
        </motion.div>

        {/* ── ERROR ── */}
        <AnimatePresence>
          {error && (
            <motion.div className="err"
              initial={{opacity:0,height:0}} animate={{opacity:1,height:'auto'}} exit={{opacity:0,height:0}}>
              <AlertTriangle size={13} style={{flexShrink:0,marginTop:2}}/>
              <pre style={{fontFamily:'inherit',whiteSpace:'pre-wrap',flex:1,fontSize:'.8rem'}}>{error}</pre>
              <button className="err-x" onClick={()=>setError(null)}><X size={12}/></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ════════════════════════════════════════
            PROGRESSION — BANNIÈRE PLEINE LARGEUR
        ════════════════════════════════════════ */}
        <motion.div variants={ITEM} className="prog-banner">
          <div className="prog-eyebrow">Progression du dossier</div>
          <StepTracker dossier={dossier}/>
        </motion.div>

        {/* ── RAPPORT STRIP ── */}
        {(dossier.statut==='RAPPORT_EXPERTISE'||closed) && (
          <motion.a variants={ITEM} className="rapport-strip"
            onClick={()=>navigate(`/dossiers/${id}/rapport`)}>
            <div className="r-icon"><FileText size={18} color="white"/></div>
            <div>
              <div className="r-title">Rapport d'expertise</div>
              <div className="r-sub">{user?.role==='MANDATAIRE'?'Consulter le rapport certifié':'Accéder · rédiger · valider'}</div>
            </div>
            <ChevronRight size={15} style={{marginLeft:'auto',color:C.gold}}/>
          </motion.a>
        )}

        {/* ════════════════════════════════════════
            BODY — 3 COLONNES DENSES
        ════════════════════════════════════════ */}
        <div className="ddp-body">

          {/* ── COL 1 : TERRAIN + DOCS ── */}
          <div>
            <motion.div variants={ITEM}>
              <div className="card">
                <CH icon={MapPin} ibg="rgba(194,82,26,.1)" ic={C.terra} title="Terrain"/>
                <div className="cb">
                  <div className="ig">
                    {[
                      ['Localisation',dossier.localisation,true],
                      ['Pays',        dossier.pays],
                      ['Ville',       dossier.ville],
                      ['Quartier',    dossier.quartier||'—'],
                      ['Superficie',  dossier.superficie?`${dossier.superficie} m²`:'—'],
                      ['Prix',        dossier.prix_achat?`${Number(dossier.prix_achat).toLocaleString('fr')} ${dossier.devise}`:'—'],
                      ['Vendeur',     dossier.nom_vendeur],
                      ['Contact',     dossier.contact_vendeur||'—'],
                      ['GPS',         dossier.coordonnees_gps||'—'],
                    ].map(([l,v,full])=>(
                      <div key={l} className={full?'ig-full':''}>
                        <div className="il">{l}</div>
                        <div className={`iv${l==='GPS'?' mono':''}`}>{v}</div>
                      </div>
                    ))}
                    {dossier.description && (
                      <div className="ig-full">
                        <div className="il">Description</div>
                        <div style={{fontSize:'.85rem',lineHeight:1.7,color:C.mist}}>{dossier.description}</div>
                      </div>
                    )}
                  </div>
                  {dossier.conclusion && (
                    <div className="conc">
                      <div className="conc-l">Conclusion finale</div>
                      <div className="conc-t">{dossier.conclusion}</div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>

            <motion.div variants={ITEM}>
              <div className="card">
                <CH icon={Paperclip} ibg="rgba(26,61,40,.08)" ic={C.forest} title="Documents" badge={docs.length}/>
                <div className="cb">
                  {!closed && <DocumentUpload dossierId={id} onUpload={fetch_}/>}
                  {docs.length===0
                    ? <div className="e-hint"><Paperclip size={16} style={{margin:'0 auto 5px',display:'block',opacity:.28}}/>Aucun document joint</div>
                    : <div className="doc-list">
                        {docs.map(doc=>(
                          <div key={doc.id} className="doc-row">
                            <div className="doc-ico"><FileText size={13}/></div>
                            <div style={{flex:1,minWidth:0}}>
                              <div className="doc-nm">{doc.nom}</div>
                              <div className="doc-mt">{doc.type_document} · {doc.taille_kb} Ko</div>
                            </div>
                            <div className="doc-acts">
                              <a className="doc-btn" href={doc.url} target="_blank" rel="noreferrer"><Eye size={11}/></a>
                              <a className="doc-btn" href={doc.url} download><Download size={11}/></a>
                            </div>
                          </div>
                        ))}
                      </div>
                  }
                </div>
              </div>
            </motion.div>
          </div>

          {/* ── COL 2 : COMMENTAIRES ── */}
          <div>
            <motion.div variants={ITEM}>
              <div className="card" style={{height:'100%'}}>
                <CH icon={MessageSquare} ibg="rgba(200,144,42,.1)" ic={C.gold} title="Commentaires" badge={dossier.commentaires?.length||0}/>
                <div className="cb">
                  {(!dossier.commentaires||dossier.commentaires.length===0)
                    ? <div className="e-hint" style={{marginBottom:12}}><MessageSquare size={16} style={{margin:'0 auto 5px',display:'block',opacity:.28}}/>Aucun commentaire</div>
                    : <div className="cmt-thread">
                        {dossier.commentaires.map(c=>(
                          <div key={c.id} className="cmt-row">
                            <div className="cmt-av">{ini(c.auteur)}</div>
                            <div className="cmt-bbl">
                              <div className="cmt-meta">
                                <span className="cmt-auth">{c.auteur?.first_name} {c.auteur?.last_name}</span>
                                <span className="cmt-dt">{dayjs(c.created_at).format('DD MMM · HH:mm')}</span>
                                {c.interne && <span className="cmt-int">Interne</span>}
                              </div>
                              <div className="cmt-txt">{c.message}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                  }
                  {!closed && (
                    <div className="cmt-compose">
                      <div className="cmt-cav">{ini(user)}</div>
                      <div style={{flex:1}}>
                        <textarea className="cmt-ta" placeholder="Ajouter un commentaire… (Ctrl+↵)"
                          value={comment} onChange={e=>setComment(e.target.value)}
                          onKeyDown={e=>{ if(e.key==='Enter'&&e.ctrlKey&&comment.trim()) run('cmt',()=>commenter(id,{message:comment}).then(()=>setComment(''))); }}/>
                      </div>
                      <button className="cmt-send"
                        onClick={()=>run('cmt',()=>commenter(id,{message:comment}).then(()=>setComment('')))}
                        disabled={!comment.trim()||al==='cmt'}>
                        {al==='cmt'?<RefreshCw size={12} className="spin"/>:<Send size={12}/>}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* ── COL 3 : ACTEURS + ACTIONS ── */}
          <div>

            {/* acteurs */}
            <motion.div variants={ITEM}>
              <div className="card">
                <CH icon={Users} ibg="rgba(26,61,40,.08)" ic={C.forest} title="Acteurs"/>
                <div className="cb">
                  <div className="act-lbl">Mandataire</div>
                  <div className="act-row">
                    <div className="act-av">{ini(dossier.mandataire)}</div>
                    <div><div className="act-nm">{dossier.mandataire?.first_name} {dossier.mandataire?.last_name}</div><div className="act-em">{dossier.mandataire?.email}</div></div>
                    <span className="act-role">Client</span>
                  </div>

                  <div className="act-lbl">Agent enquêteur</div>
                  {dossier.agent
                    ? <div className="act-row">
                        <div className="act-av" style={{background:`linear-gradient(135deg,${C.terra},${C.gold})`}}>{ini(dossier.agent)}</div>
                        <div><div className="act-nm">{dossier.agent.first_name} {dossier.agent.last_name}</div><div className="act-em">{dossier.agent.email}</div></div>
                        <span className="act-role">Agent</span>
                      </div>
                    : <div className="act-none"><AlertTriangle size={12}/> Non assigné</div>
                  }

                  {user?.role==='ADMIN' && !closed && (
                    <>
                      <div className="hdiv"/>
                      <div className="act-lbl">{dossier.agent?"Changer d'agent":'Assigner un agent'}</div>
                      <div className="frow">
                        <select className="fs" value={agentId} onChange={e=>setAgentId(e.target.value)}>
                          <option value="">Choisir…</option>
                          {agents.map(a=><option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
                        </select>
                        <button className="ba ba-f" style={{width:'auto',padding:'0 14px',marginBottom:0}}
                          onClick={()=>run('ag',()=>assignerAgent(id,{agent_id:agentId}).then(()=>setAgentId('')))}
                          disabled={!agentId||al==='ag'}>
                          {al==='ag'?<RefreshCw size={12} className="spin"/>:'OK'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>

            {/* AGENT : terminer */}
            {user?.role==='AGENT' && !closed && dossier.statut!=='RAPPORT_EXPERTISE' && (
              <motion.div variants={ITEM}>
                <div className={`card ${dossier.etape_terminee?'bdr-gold':'bdr-forest'}`}>
                  <CH icon={ClipboardCheck}
                    ibg={dossier.etape_terminee?'rgba(200,144,42,.14)':'rgba(26,61,40,.1)'}
                    ic={dossier.etape_terminee?C.gold:C.forest}
                    title={dossier.etape_terminee?"En attente":"Terminer l'étape"}/>
                  <div className="cb">
                    {dossier.etape_terminee
                      ? <div className="wbox">
                          <div className="w-ic"><Clock size={19}/></div>
                          <div className="w-tt">Étape soumise</div>
                          <p className="w-sb">L'admin va valider et passer à la suite.</p>
                        </div>
                      : <>
                          <label className="fl">Rapport de l'étape</label>
                          <textarea className="fta" placeholder="Constatations, résultats…" value={termine} onChange={e=>setTermine(e.target.value)}/>
                          <button className="ba ba-f" onClick={()=>run('ter',()=>terminerEtape(id,{commentaire:termine}).then(()=>setTermine('')))}
                            disabled={al==='ter'}>
                            {al==='ter'?<RefreshCw size={13} className="spin"/>:<><CheckCircle size={13}/> Marquer terminée</>}
                          </button>
                        </>
                    }
                  </div>
                </div>
              </motion.div>
            )}

            {/* ADMIN : valider */}
            {user?.role==='ADMIN' && dossier.etape_terminee && !closed && dossier.statut!=='RAPPORT_EXPERTISE' && (
              <motion.div variants={ITEM}>
                <div className="card bdr-green">
                  <CH icon={Shield} ibg="rgba(47,102,69,.12)" ic={C.forestLt} title="Valider l'étape"/>
                  <div className="cb">
                    <p style={{fontSize:'.8rem',color:C.mist,marginBottom:10,lineHeight:1.6}}>
                      {dossier.agent?`${dossier.agent.first_name} a terminé`:'Prête'} : <strong style={{color:C.forest}}>{dossier.statut.replace(/_/g,' ')}</strong>
                    </p>
                    <label className="fl">Commentaire</label>
                    <textarea className="fta" placeholder="Notes (optionnel)…" value={valide.commentaire} onChange={e=>setValide({...valide,commentaire:e.target.value})}/>
                    <button className="ba ba-gr" onClick={()=>run('val',()=>validerEtape(id,{commentaire:valide.commentaire}).then(()=>setValide({commentaire:'',resultat:'',conclusion:''})))}
                      disabled={al==='val'}>
                      {al==='val'?<RefreshCw size={13} className="spin"/>:<><CheckCircle size={13}/> Valider et continuer</>}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ADMIN : clôturer */}
            {user?.role==='ADMIN' && dossier.statut==='RAPPORT_EXPERTISE' && (
              <motion.div variants={ITEM}>
                <div className="card bdr-terra">
                  <CH icon={Lock} ibg="rgba(194,82,26,.1)" ic={C.terra} title="Clôturer le dossier"/>
                  <div className="cb">
                    <p style={{fontSize:'.8rem',color:C.mist,marginBottom:10,lineHeight:1.6}}>Rapport envoyé. Définissez le résultat et clôturez.</p>
                    <label className="fl">Résultat *</label>
                    <select className="fs" value={cloture.resultat} onChange={e=>setCloture({...cloture,resultat:e.target.value})}>
                      <option value="">Sélectionner…</option>
                      <option value="VALIDE">✓ Validé — achat sécurisé</option>
                      <option value="LITIGE">⚠ Litige détecté</option>
                      <option value="RISQUE">🔶 Risque identifié</option>
                    </select>
                    <label className="fl">Conclusion</label>
                    <textarea className="fta" placeholder="Conclusion finale…" value={cloture.conclusion} onChange={e=>setCloture({...cloture,conclusion:e.target.value})}/>
                    <button className="ba ba-t" onClick={()=>run('clo',()=>cloturerDossier(id,cloture).then(()=>setCloture({resultat:'',conclusion:''})))}
                      disabled={!cloture.resultat||al==='clo'}>
                      {al==='clo'?<RefreshCw size={13} className="spin"/>:<><Lock size={13}/> Clôturer</>}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* MANDATAIRE : décision */}
            {user?.role==='MANDATAIRE' && closed && (
              <motion.div variants={ITEM}>
                <div className={`card ${dossier.decision_mandataire?'bdr-green':'bdr-gold'}`}>
                  <CH icon={dossier.decision_mandataire?CheckCircle:MessageSquare}
                    ibg={dossier.decision_mandataire?'rgba(47,102,69,.12)':'rgba(200,144,42,.14)'}
                    ic={dossier.decision_mandataire?C.forestLt:C.gold}
                    title="Votre décision"/>
                  <div className="cb">
                    {dossier.decision_mandataire
                      ? (()=>{ const ds=DECISION_STYLE[dossier.decision_mandataire]||DECISION_STYLE.INFO_SUPP; return (
                          <div className="dec" style={{background:ds.bg}}>
                            <div className="dec-ic" style={{background:ds.bg,color:ds.color}}>{ds.icon}</div>
                            <div>
                              <div className="dec-lb" style={{color:ds.color}}>{ds.label}</div>
                              {dossier.commentaire_mandataire && <div className="dec-cm">{dossier.commentaire_mandataire}</div>}
                              {dossier.decision_mandataire_at && <div className="dec-dt">{dayjs(dossier.decision_mandataire_at).format('DD MMM YYYY · HH:mm')}</div>}
                            </div>
                          </div>
                        ); })()
                      : <>
                          <p style={{fontSize:'.8rem',color:C.mist,marginBottom:10,lineHeight:1.6}}>Suite au rapport, quelle est votre décision ?</p>
                          <label className="fl">Décision *</label>
                          <select className="fs" value={decision.decision} onChange={e=>setDecision({...decision,decision:e.target.value})}>
                            <option value="">Sélectionner…</option>
                            <option value="ACCORD">🤝 Je souhaite acheter</option>
                            <option value="RENONCIATION">❌ Je renonce à l'achat</option>
                            <option value="NEGOCIATION">💬 Je veux négocier</option>
                            <option value="INFO_SUPP">❓ Infos complémentaires</option>
                          </select>
                          <label className="fl">Commentaire</label>
                          <textarea className="fta" placeholder="Précisez si nécessaire…" value={decision.commentaire} onChange={e=>setDecision({...decision,commentaire:e.target.value})}/>
                          <button className="ba ba-g" onClick={()=>run('rep',()=>repondreDossier(id,decision).then(()=>setDecision({decision:'',commentaire:''})))}
                            disabled={!decision.decision||al==='rep'}>
                            {al==='rep'?<RefreshCw size={13} className="spin"/>:<><Send size={13}/> Envoyer</>}
                          </button>
                        </>
                    }
                  </div>
                </div>
              </motion.div>
            )}

            {/* ADMIN/AGENT : voir décision mandataire */}
            {['ADMIN','AGENT'].includes(user?.role) && closed && dossier.decision_mandataire && (
              <motion.div variants={ITEM}>
                <div className="card bdr-green">
                  <CH icon={Users} ibg="rgba(47,102,69,.12)" ic={C.forestLt} title="Décision du mandataire"/>
                  <div className="cb">
                    {(()=>{ const ds=DECISION_STYLE[dossier.decision_mandataire]||DECISION_STYLE.INFO_SUPP; return (
                      <div className="dec" style={{background:ds.bg}}>
                        <div className="dec-ic" style={{background:ds.bg,color:ds.color}}>{ds.icon}</div>
                        <div>
                          <div className="dec-lb" style={{color:ds.color}}>{ds.label}</div>
                          {dossier.commentaire_mandataire && <div className="dec-cm">{dossier.commentaire_mandataire}</div>}
                          {dossier.decision_mandataire_at && <div className="dec-dt">{dayjs(dossier.decision_mandataire_at).format('DD MMM YYYY · HH:mm')}</div>}
                        </div>
                      </div>
                    ); })()}
                  </div>
                </div>
              </motion.div>
            )}

          </div>{/* end col3 */}
        </div>{/* end body */}
      </motion.div>
    </div>
  );
}